import { BREAKPOINT_DESKTOP, useBreakpoints } from "../../constants/constants";
import { HubletPageStyles } from "../../components/Dashboard/HubletView/HubletViewStyles/HubletPageStyles";
import { Resizable } from "re-resizable";
import { TrendChartBinSizesEnum } from "../../types/enums";
import { useResetTrendChartBinSize } from "../DashboardPages/dashboardPagesUtils";
import { Fade, useMediaQuery } from "@mui/material";
import DatepickerAndCharts, {
  PnlSummaryChartCardHubletPage,
} from "./DatepickerAndCharts";
import {
  useCurrentShift,
  useFocusRange,
  useGetPageFromPath,
  usePnlPopupTimeRange,
} from "utils/customHooks/genericHooks";
import { useIsRetrotagPopperOpen } from "utils/customHooks/retrotagSliceSelectors";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useElementBoundingClientRect } from "./useElementBoundingClientRect";
import { getScaleFromWidthForLargeScreens } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import {
  hasUserClickedMinimodeButtonAtom,
  useIsMiniMode,
} from "components/Dashboard/HubletView/hubletViewUtils";
import { useEvent, useLocalStorage, useMount } from "react-use";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useEffect, useState } from "react";
import HubletView from "components/Dashboard/HubletView/HubletView";
import CurrentRateTracker from "components/Dashboard/HubletView/CurrentRateTracker";
import { useAtom } from "jotai";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useWasLastPageOperatorPage } from "store/atoms";
import { CUSTOM_SCROLLBAR_CSS } from "App/CUSTOM_SCROLLBAR_CSS";
import styled, { css } from "styled-components/macro";

const RESIZABLE_ALL_DISABLED = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  topRight: false,
  bottomRight: false,
  bottomLeft: false,
  topLeft: false,
};

const HubletPage = () => {
  return (
    <HubletPageStylesAndEffectsProvider>
      <ResizableHubletView />
      <ErrorBoundary id="DatepickerAndCharts">
        <DatepickerAndCharts />
      </ErrorBoundary>
    </HubletPageStylesAndEffectsProvider>
  );
};

function HubletPageStylesAndEffectsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // set trend chart to hourly bin size (only one allowed on Dashboard Pages)
  useResetTrendChartBinSize({
    newTrendChartBinSize: TrendChartBinSizesEnum.hours,
  });

  const { isDesktopOrLarger } = useBreakpoints();

  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const processId = useProcessIdOnSingleProcessPage();
  const isRetrotagPopperOpen = useIsRetrotagPopperOpen(processId);

  const datePickerAndChartsRect = useElementBoundingClientRect(
    "[class*=DatepickerAndChartsStyles]"
  );
  const datepickerAndChartsContainerWidth = datePickerAndChartsRect?.width;

  const scale = datepickerAndChartsContainerWidth
    ? getScaleFromWidthForLargeScreens(datepickerAndChartsContainerWidth)
    : 1;

  // allow user to resize the HubletView, and store size in localStorage
  const { innerWidth } = useWindowSize();
  const _initialWidth = isDesktopOrLarger ? innerWidth / 2 : innerWidth;
  const [widthSinceMouseup, setInitialWidth] = useState(_initialWidth);
  const [hubletViewWidth, setWidth] = useLocalStorage("hubletViewWidth", {
    width: widthSinceMouseup,
    lastInnerWidth: innerWidth,
  });
  // reset HubletView size when window size changes
  useEvent("resize", () => {
    setWidth({ width: _initialWidth, lastInnerWidth: innerWidth });
    setInitialWidth(_initialWidth);
  });
  // reset HubletView size on mount if window has been resized smaller since last opened e.g. close flight tab, resize browser, reopen flight
  useMount(() => {
    const hasBrowserTabBeenClosedThenResizedThenOpened =
      hubletViewWidth &&
      hubletViewWidth.lastInnerWidth &&
      innerWidth < hubletViewWidth.lastInnerWidth;
    if (hasBrowserTabBeenClosedThenResizedThenOpened) {
      setWidth({ width: innerWidth, lastInnerWidth: innerWidth });
      setInitialWidth(_initialWidth);
    }
  });
  useSetMiniModeWhenViewingPastData();

  const [, setWasLastPageOperatorPage] = useWasLastPageOperatorPage();
  useEffect(() => {
    setWasLastPageOperatorPage(false);
  }, [setWasLastPageOperatorPage]);

  return (
    <HubletPageStyles
      $scale={scale}
      data-component-name={"Hublet Page"}
      $isPnlPopupOpen={Boolean(pnlPopupTimeRange)}
      $isRetrotagPopperOpen={isRetrotagPopperOpen}
      key={innerWidth}
    >
      {children}
    </HubletPageStyles>
  );
}

export default HubletPage;

function ResizableHubletView() {
  // set trend chart to hourly bin size (only one allowed on Dashboard Pages)
  useResetTrendChartBinSize({
    newTrendChartBinSize: TrendChartBinSizesEnum.hours,
  });

  const { isTabletOrLarger, isDesktopOrLarger } = useBreakpoints();
  const isVerticalLayout = isTabletOrLarger && !isDesktopOrLarger;

  const shouldEnableResize = isDesktopOrLarger;
  const isPrint = useMediaQuery("print");

  // allow user to resize the HubletView, and store size in localStorage
  const { innerWidth } = useWindowSize();
  const _initialWidth = isDesktopOrLarger ? innerWidth / 2 : innerWidth;
  const [widthSinceMouseup, setInitialWidth] = useState(_initialWidth);
  const [hubletViewWidth, setWidth] = useLocalStorage("hubletViewWidth", {
    width: widthSinceMouseup,
    lastInnerWidth: innerWidth,
  });
  const [isMiniMode] = useIsMiniMode();
  // reset HubletView size when window size changes
  useEvent("resize", () => {
    setWidth({ width: _initialWidth, lastInnerWidth: innerWidth });
    setInitialWidth(_initialWidth);
  });
  // reset HubletView size on mount if window has been resized smaller since last opened e.g. close flight tab, resize browser, reopen flight
  useMount(() => {
    const hasBrowserTabBeenClosedThenResizedThenOpened =
      hubletViewWidth &&
      hubletViewWidth.lastInnerWidth &&
      innerWidth < hubletViewWidth.lastInnerWidth;
    if (hasBrowserTabBeenClosedThenResizedThenOpened) {
      setWidth({ width: innerWidth, lastInnerWidth: innerWidth });
      setInitialWidth(_initialWidth);
    }
  });
  useSetMiniModeWhenViewingPastData();
  const [hasResized, setHasResized] = useState(false);

  const [, setWasLastPageOperatorPage] = useWasLastPageOperatorPage();
  useEffect(() => {
    setWasLastPageOperatorPage(false);
  }, [setWasLastPageOperatorPage]);

  return (
    <StyledResizable
      $isMiniMode={isMiniMode}
      className="HubletView-resizable-container"
      onResize={(e, direction, ref, d) => {
        setHasResized(true);
        setWidth({
          width: widthSinceMouseup + d.width,
          lastInnerWidth: innerWidth,
        });
      }}
      onResizeStop={(e, direction, ref, d) => {
        setInitialWidth(hubletViewWidth?.width ?? _initialWidth);
      }}
      handleClasses={{ right: "resize-handle" }}
      style={{
        flexGrow: 0,
        flexShrink: 0,
        margin: isPrint ? "1em auto" : "auto",
      }}
      enable={{
        ...RESIZABLE_ALL_DISABLED,
        ...(shouldEnableResize ? { right: true } : {}),
      }}
      defaultSize={{
        // not setting default size bugs the interface (starts off smaller than minimum)
        width: `${
          isVerticalLayout /* vertical layout - HubletView is centered at the top */
            ? innerWidth
            : (hubletViewWidth?.width ?? _initialWidth)
        }px`,
        height: isDesktopOrLarger ? "100vh" : "fit-content",
      }}
      size={{
        width: `${hubletViewWidth?.width ?? _initialWidth}px`,
        height: isDesktopOrLarger ? "100vh" : "fit-content",
      }}
      {...(shouldEnableResize
        ? {
            handleStyles: { right: { width: 23, right: -11, zIndex: 1 } }, // make the "grabbable" part of the resize box larger so fat fingers can hit it
            minWidth: "15%", // this is the default smallest window size for the timeline and rollup
            maxWidth: "50%",
          }
        : {})}
    >
      <ErrorBoundary id="HubletView">
        <HubletView hasResized={hasResized} />
      </ErrorBoundary>
      <ErrorBoundary id="MiniModePnlSummaryChart">
        <Fade
          in={isMiniMode && isDesktopOrLarger}
          timeout={500}
          unmountOnExit={true}
        >
          <div style={{ marginRight: -10 }}>
            <PnlSummaryChartCardHubletPage />
          </div>
        </Fade>
      </ErrorBoundary>

      <ErrorBoundary small={true} overlay={true} id="CurrentRateTracker">
        <CurrentRateTracker />
      </ErrorBoundary>
    </StyledResizable>
  );
}

const StyledResizable = styled(Resizable)<{
  $isMiniMode: boolean;
}>`
  @media (min-width: ${BREAKPOINT_DESKTOP}px) {
    ${props =>
      props.$isMiniMode
        ? css`
            ${CUSTOM_SCROLLBAR_CSS}
            padding-right: 0 !important;
          `
        : ""}
  }
`;

function useSetMiniModeWhenViewingPastData() {
  const [isMiniMode, setIsMiniMode] = useIsMiniMode();
  // if user has clicked, disable automatic behaviour
  const [hasUserClickedButton, setHasUserClickedButton] = useAtom(
    hasUserClickedMinimodeButtonAtom
  );
  useMount(() => {
    setHasUserClickedButton(false);
  });

  const focusRange = useFocusRange();
  const { isTabletOrLarger } = useBreakpoints();
  const currentShift = useCurrentShift();
  const ready = focusRange && isTabletOrLarger && currentShift;
  const { isHubletPageCompactLayoutDisabled } = useSettings();
  // when focusRange is before today, set mini mode (unless user has clicked to disable minimode)
  useEffect(() => {
    if (!ready || hasUserClickedButton || isHubletPageCompactLayoutDisabled) {
      return;
    }
    const isFocusRangeBeforeCurrentShiftStart =
      focusRange.lt <= currentShift.range.ge;
    setIsMiniMode(isFocusRangeBeforeCurrentShiftStart);
  }, [
    isMiniMode,
    focusRange,
    setIsMiniMode,
    ready,
    hasUserClickedButton,
    currentShift,
    isHubletPageCompactLayoutDisabled,
  ]);
  // minimode is disabled in <tablet breakpoint & on non-hublet pages (i.e. resets back to default when navigating away)
  const { isHubletPage } = useGetPageFromPath();
  useEffect(() => {
    if (isTabletOrLarger && isHubletPage) {
      return;
    }
    if (isMiniMode) {
      setIsMiniMode(false);
    }
  }, [isMiniMode, isTabletOrLarger, setIsMiniMode, isHubletPage]);
}
